/* Offline write queue.
 *
 * When the user is offline and performs a whitelisted mutation (currently:
 * creating an expense or saving leg parameters/log), we push the request
 * details into IndexedDB instead of failing. The next time the device comes
 * online — or the user clicks "Sincronizar" — `flushOutbox` walks the queue
 * and replays each request via the same axios client used elsewhere.
 *
 * Whitelist of "safe to queue" endpoints is defined in `isQueueable`. Each
 * queued item carries a client-generated `idempotency_key` (UUID v4) that
 * we surface as the `Idempotency-Key` HTTP header on replay. The backend
 * can opt-in to dedupe on this key in a follow-up — until then the header
 * is harmless. This is the client half of a two-half fix; without it the
 * server can't dedupe even if it wanted to (network ACK loss → replay →
 * duplicate writes on mobile).
 *
 * Listeners can subscribe to outbox-count changes via subscribePending().
 */
import {
  enqueueOutbox,
  listOutbox,
  countPendingOutbox,
  markOutboxResult,
  deleteOutboxEntry,
} from "./offline-db";

const QUEUEABLE = [
  { method: "post", re: /^\/expenses(\?|$)/ },
  { method: "put", re: /^\/legs\/[^/?]+(\?|$)/ },
  // Flight parameters (idempotent upsert) + post-flight log. Both are filled
  // by pilots who routinely lose signal at the ramp / in the air, so autosave
  // must queue instead of failing. `/parameters` replays safely (one record
  // per leg); `/post-flight` carries an Idempotency-Key for the eventual
  // server-side dedupe of lost-ACK replays.
  { method: "post", re: /^\/legs\/[^/?]+\/parameters(\?|$)/ },
  { method: "post", re: /^\/legs\/[^/?]+\/post-flight(\?|$)/ },
];

// Hard cap on replay attempts before we mark an entry as permanently failed.
// 5 covers transient network hiccups + a couple of 5xx retries; beyond that
// the request is unlikely to ever succeed (server contract changed, trip
// deleted, validation drift) and should surface to the user instead of
// living forever in the outbox badge counter.
const MAX_REPLAY_ATTEMPTS = 5;

const _listeners = new Set();
let _flushing = false;

/** True when the axios `data` payload can't be structured-cloned into
 *  IndexedDB. `FormData` (used by every multipart receipt upload) is the
 *  canonical case — IDB silently throws DataCloneError on it. We MUST refuse
 *  to queue these, otherwise the toast says "encolado" while the data is
 *  actually lost. */
function _isUnserializableForIDB(data) {
  if (data == null) return false;
  if (typeof FormData !== "undefined" && data instanceof FormData) return true;
  if (typeof Blob !== "undefined" && data instanceof Blob) return true;
  if (typeof File !== "undefined" && data instanceof File) return true;
  return false;
}

export function isQueueable(method, url, data) {
  const m = (method || "").toLowerCase();
  const path = url.replace(/^https?:\/\/[^/]+/, "").replace(/^\/api/, "");
  if (!QUEUEABLE.some((rule) => rule.method === m && rule.re.test(path))) {
    return false;
  }
  // Multipart payloads (receipts, doc uploads) can't be persisted to IDB;
  // refuse to queue and let the caller surface a real error so the user
  // doesn't get a silent "saved" lie.
  if (_isUnserializableForIDB(data)) return false;
  return true;
}

// UUID v4 generator that prefers the standard Web Crypto helper and falls
// back to a manual implementation for older browsers / jsdom environments
// where `crypto.randomUUID` may be missing. The backend only needs a
// reasonably-unique string — RFC 4122 conformance isn't load-bearing.
function _newIdempotencyKey() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {}
  // Fallback: RFC4122-style v4 string built from Math.random.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Add a request to the outbox queue. `entry.summary` is shown in the
 * pending-changes UI so the user knows what's waiting.
 *
 * `user_id` binds the entry to the user who created it. On replay we drop
 * entries that don't match the currently-logged-in user — otherwise a
 * pilot's queued expense would replay under the next user to log in on the
 * same device, creating financial records under the wrong identity. */
export async function queueRequest({ method, url, data, headers, summary, user_id }) {
  await enqueueOutbox({
    method: (method || "get").toLowerCase(),
    url,
    data: data || null,
    headers: headers || null,
    summary: summary || `${(method || "GET").toUpperCase()} ${url}`,
    kind: classify(method, url),
    idempotency_key: _newIdempotencyKey(),
    user_id: user_id || null,
  });
  await _notifyListeners();
}

/** Read the current logged-in user id from the AuthContext cache. We can't
 *  import AuthContext here (circular) so we read its localStorage hand-off
 *  written by `_writeCachedUser`. Returns null on missing/corrupt cache. */
function _readCurrentUserId() {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem("altiv_user_cache");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.id || null;
  } catch {
    return null;
  }
}

function classify(method, url) {
  const path = (url || "").replace(/^\/api/, "");
  if (/^\/expenses/.test(path)) return "expense";
  if (/^\/legs\//.test(path)) return "leg_params";
  return "unknown";
}

/** Replay all pending entries, oldest first. Skips while already flushing
 * so concurrent triggers (online event + manual click) don't double-fire.
 *
 * `opts.currentUserId` (optional) — when provided, entries queued by a
 * DIFFERENT user are dropped instead of replayed, so we never write a
 * previous user's data under the current bearer token. */
export async function flushOutbox(api, opts = {}) {
  if (_flushing) return { skipped: true };
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { skipped: true, reason: "offline" };
  }
  const currentUserId = opts.currentUserId || _readCurrentUserId();
  _flushing = true;
  let sent = 0;
  let failed = 0;
  let dropped_foreign = 0;
  try {
    const entries = await listOutbox();
    const pending = entries
      .filter((e) => e.status === "pending")
      .sort((a, b) => (a.queued_at > b.queued_at ? 1 : -1));

    for (const entry of pending) {
      // Cross-user guard: drop entries whose `user_id` doesn't match the
      // currently-logged-in user. Legacy entries without `user_id` are
      // grandfathered through (pre-fix data) — but only if we DON'T have a
      // current user (boot before AuthContext hydrates); if we have one,
      // the safe default is to drop the legacy entry too.
      if (entry.user_id && currentUserId && entry.user_id !== currentUserId) {
        await deleteOutboxEntry(entry.id);
        dropped_foreign += 1;
        continue;
      }
      if (!entry.user_id && currentUserId) {
        // Pre-fix entry on a device that now has a logged-in user — we can't
        // confirm ownership, so refuse to replay. Drop rather than retain
        // forever (badge would lie).
        await deleteOutboxEntry(entry.id);
        dropped_foreign += 1;
        continue;
      }

      try {
        // Stamp Idempotency-Key from the queued item so the backend (when
        // it adds dedupe) can drop duplicate replays caused by lost ACKs.
        const replayHeaders = { ...(entry.headers || {}) };
        if (entry.idempotency_key) {
          replayHeaders["Idempotency-Key"] = entry.idempotency_key;
        }
        await api.request({
          method: entry.method,
          url: entry.url,
          data: entry.data,
          headers: replayHeaders,
          // Skip the offline-cache fallback when replaying — we want a real
          // network round-trip, not a fake "served from snapshot" response.
          _skipOfflineFallback: true,
          // Don't tear down the session on a 401 during silent replay:
          // the auth interceptor would wipe the JWT and redirect, which is
          // disastrous when the user just opened the app online. Instead
          // let the entry move to `failed` via the normal error path.
          _skipNotifyOnFail: true,
        });
        await deleteOutboxEntry(entry.id);
        sent += 1;
      } catch (err) {
        // 4xx is a permanent failure — the request shape, the resource, or
        // the user's permission to write it has changed and replay will
        // never succeed. Surface it to the user instead of looping forever.
        const status = err?.response?.status;
        const isPermanent = typeof status === "number" && status >= 400 && status < 500;
        const attempts = (entry.attempts || 0) + 1;
        const exhausted = attempts >= MAX_REPLAY_ATTEMPTS;
        await markOutboxResult(entry.id, {
          status: isPermanent || exhausted ? "failed" : "pending",
          error: err?.message || String(err),
        });
        failed += 1;
      }
    }
  } finally {
    _flushing = false;
    await _notifyListeners();
  }
  // Confirmación al usuario: sus cambios offline ya quedaron en el server.
  // Solo si efectivamente se subió algo — no spamear cuando la cola está vacía.
  if (sent > 0) {
    try {
      const { notifySynced } = await import("@/components/ConnectivityBanner");
      notifySynced(sent);
    } catch {}
  }
  return { sent, failed, dropped_foreign };
}

export async function getPendingCount() {
  return countPendingOutbox();
}

export async function listAllOutbox() {
  return listOutbox();
}

export async function discardOutboxEntry(id) {
  await deleteOutboxEntry(id);
  await _notifyListeners();
}

export function subscribePending(cb) {
  _listeners.add(cb);
  // Fire once with current counts so subscribers can render immediately.
  _readCounts()
    .then(cb)
    .catch(() => {});
  return () => _listeners.delete(cb);
}

/** Return `{pending, failed}` so subscribers can render both states without
 *  a second IDB read. `pending` drives the auto-sync badge; `failed` drives
 *  the "no se pudieron subir" surface introduced by OFF-6. */
async function _readCounts() {
  try {
    const all = await listOutbox();
    return {
      pending: all.filter((e) => e.status === "pending").length,
      failed: all.filter((e) => e.status === "failed").length,
    };
  } catch {
    return { pending: 0, failed: 0 };
  }
}

async function _notifyListeners() {
  if (_listeners.size === 0) return;
  const counts = await _readCounts();
  _listeners.forEach((cb) => {
    try {
      cb(counts);
    } catch {}
  });
}

/** Return every outbox entry currently in `failed` state — UI uses this to
 *  render the discard popover so the badge counter doesn't lie forever. */
export async function listFailedEntries() {
  try {
    const all = await listOutbox();
    return all.filter((e) => e.status === "failed");
  } catch {
    return [];
  }
}

// Auto-flush on reconnect.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    // Lazy-import api to avoid a circular dep at module load time.
    // Swallow rejections: flushOutbox handles its own per-entry errors, but an
    // import failure or unexpected throw here would otherwise surface as an
    // unhandled rejection on every reconnect.
    import("./api").then(({ default: api }) => flushOutbox(api)).catch(() => {});
  });
}
